import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { get, isNull } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import {
  buildCoreConfigurationsPath,
  InventoryCoreConfigurationEntities,
  InventoryCoreConfigurationsModel,
} from 'client/data/models/inventory-core-configurations';
import { IncentivesEntities, IncentiveModel, getIncentivesPath } from 'client/data/models/incentives';
import { getUnifiedLeadFormDealerCountV2Path, UnifiedLeadFormModel } from 'client/data/models/unified-lead-form';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { srpLinkBuilder } from 'site-modules/shared/utils/srp-link-constructor';
import { Link } from 'site-modules/shared/components/link/link';
import { TransparentPricingLeadFormDrawer } from 'site-modules/shared/components/lead-form/transparent-pricing-lead-form/transparent-pricing-lead-form-drawer';
import { IncentivesNarrativeModel } from 'site-modules/shared/components/incentives/incentives-narrative-model/incentives-narrative-model';
import { NoIncentivesExperience } from 'site-modules/incentives/components/no-incentives-experience/no-incentives-experience';
import { getLeadFormCreativeId } from 'site-modules/shared/components/lead-form/utils/lead-form-creative-id';
import { filterPanelIncentives } from 'site-modules/incentives/utils/incentives';
import { LEAD_FORMS_CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { LEAD_FORM_CONFIGS } from 'site-modules/shared/constants/lead-form/lead-form-configs';
import { IncentivesPanelCategories } from './incentives-panel-categories';

import './incentives-panel.scss';

const OFFERS_BTN_CLASS_NAME = 'px-2 py-1 me-0_5 dealer-offers-btn text-transform-none fw-normal text-white';
export const INCENTIVES_PANEL_NAME = 'incentives-panel';

export class IncentivesPanelUI extends Component {
  static propTypes = {
    location: VisitorEntities.Location.isRequired,
    incentives: IncentivesEntities.IncentivesGraphql,
    coreConfigurations: InventoryCoreConfigurationEntities.CoreConfigurations,
    selectedStyle: VehicleEntities.Style,
    makeModelSubmodelYear: VehicleEntities.MakeModelSubmodelYear,
    leadFormDealerCount: PropTypes.number,
    selectedIncentiveType: PropTypes.string,
    isMobile: PropTypes.bool,
    pageNameLegacy: PropTypes.string,
  };

  static defaultProps = {
    incentives: null,
    coreConfigurations: null,
    selectedStyle: null,
    makeModelSubmodelYear: null,
    leadFormDealerCount: null,
    selectedIncentiveType: '',
    isMobile: false,
    pageNameLegacy: '',
  };

  constructor(props) {
    super(props);

    this.state = {
      isDrawerOpened: false,
      coreConfigurations: this.props.coreConfigurations || [],
    };
  }

  static getDerivedStateFromProps({ coreConfigurations }, { coreConfigurations: prevCoreConfigurations }) {
    if (!isNull(coreConfigurations) && coreConfigurations !== prevCoreConfigurations) {
      return { coreConfigurations };
    }
    return null;
  }

  shouldComponentUpdate({ incentives }) {
    return !!incentives;
  }

  toggleDrawer = () => {
    this.setState({
      isDrawerOpened: !this.state.isDrawerOpened,
    });
  };

  render() {
    const {
      location,
      selectedStyle,
      selectedIncentiveType,
      leadFormDealerCount,
      incentives,
      makeModelSubmodelYear,
      isMobile,
      pageNameLegacy,
    } = this.props;
    const { isDrawerOpened, coreConfigurations } = this.state;
    if (!incentives) {
      return null;
    }
    const { trim, name, id } = selectedStyle;
    const configuration = coreConfigurations.find(({ id: styleId }) => id === parseInt(styleId, 10)) || selectedStyle;
    const hasInventory = !!get(configuration, 'inventoryCount');
    const hasLeadForm = !!(hasInventory || leadFormDealerCount);
    const srpLink = srpLinkBuilder({
      inventorytype: 'new',
      make: get(selectedStyle, 'make.name'),
      model: get(selectedStyle, 'model.name'),
      year: get(selectedStyle, 'year'),
      submodelid: get(selectedStyle, 'submodel.name'),
      trim: hasInventory ? configuration.trim : '',
    });
    const srpLinkNoInventory = srpLinkBuilder({
      make: get(selectedStyle, 'make.slug'),
      model: get(selectedStyle, 'model.slug'),
      radius: 200,
    });
    const creativeId = getLeadFormCreativeId({
      isNewVin: true,
      pageNameLegacy,
      formName: LEAD_FORMS_CREATIVE_ID.DEALS_FORM,
    });

    return (
      <Fragment>
        <ScrollElement
          id={INCENTIVES_PANEL_NAME}
          data-test="incentives-panel"
          className={classnames('incentives-panel text-gray-darker py-1_5 px-1 mb-2 mb-md-1_5 bg-gray-lightest', {
            'no-space': isMobile,
          })}
        >
          {incentives.numIncentives ? (
            <Fragment>
              <div
                className="mb-2 d-flex flex-column flex-md-row justify-content-between align-items-md-end"
                data-tracking-parent="cash_offers_incentives"
              >
                <div className="mb-1_5 mb-md-0">
                  {trim && <h2 className="heading-4 mb-0_25">{trim}</h2>}
                  {name && <span>{name}</span>}
                </div>
                {hasLeadForm ? (
                  <Button
                    color="primary-b"
                    size="lg"
                    className={OFFERS_BTN_CLASS_NAME}
                    onClick={this.toggleDrawer}
                    rel="nofollow"
                    data-tracking-id="view_pricing_details"
                    data-hidedelightedoninteraction
                  >
                    Compare dealer offers
                  </Button>
                ) : (
                  <Button
                    tag={Link}
                    to={srpLinkNoInventory}
                    color="primary-b"
                    size="lg"
                    className={OFFERS_BTN_CLASS_NAME}
                    rel="nofollow"
                    data-tracking-id="view_search_result"
                  >
                    See all for sale
                  </Button>
                )}
              </div>
              <IncentivesPanelCategories
                selectedIncentiveType={selectedIncentiveType}
                incentives={incentives}
                isMobile={isMobile}
              />
              <IncentivesNarrativeModel />
            </Fragment>
          ) : (
            <NoIncentivesExperience makeModelSubmodelYear={makeModelSubmodelYear} />
          )}
        </ScrollElement>

        {hasLeadForm && (
          <TransparentPricingLeadFormDrawer
            isOpen={isDrawerOpened}
            onDrawerToggle={this.toggleDrawer}
            params={{
              styleId: get(configuration, 'id'),
              trim: get(configuration, 'trim'),
              srpLink,
              vehicle: makeModelSubmodelYear,
              zipCode: location.zipCode,
              city: location.city,
            }}
            options={{
              coreConfiguration: configuration,
              thankYouLayoutOptions: { hideSrpLink: true },
              isMobile,
              creativeId,
            }}
            creativeId={creativeId}
          />
        )}
      </Fragment>
    );
  }
}

const IncentivesPanelWrapper = connectToModel(IncentivesPanelUI, {
  leadFormDealerCount: bindToPath(
    ({ coreConfigurations, selectedStyle, incentives, incentivesGraphql }) =>
      !!get(incentives, 'numIncentives', get(incentivesGraphql, 'numIncentives')) &&
      (!get(coreConfigurations, 'length') ||
        !get(
          coreConfigurations.find(({ id: styleId }) => get(selectedStyle, 'id') === parseInt(styleId, 10)),
          'inventoryCount'
        )) &&
      getUnifiedLeadFormDealerCountV2Path({
        makeSlug: get(selectedStyle, 'make.slug'),
        modelSlug: get(selectedStyle, 'model.slug'),
        strategy: LEAD_FORM_CONFIGS.QUICK_QUOTES_TRIM_DST.strategy,
      }),
    UnifiedLeadFormModel
  ),
});

export const IncentivesPanel = connectToModel(IncentivesPanelWrapper, {
  location: bindToPath('location', VisitorModel),
  incentives: bindToPath(
    ({ selectedStyle }) => getIncentivesPath(get(selectedStyle, 'id')),
    IncentiveModel,
    filterPanelIncentives
  ),
  coreConfigurations: bindToPath(
    ({ selectedStyle }) =>
      buildCoreConfigurationsPath({
        make: get(selectedStyle, 'make.slug'),
        model: get(selectedStyle, 'model.slug'),
        submodel: get(selectedStyle, 'submodels.slug'),
        year: get(selectedStyle, 'year'),
      }),
    InventoryCoreConfigurationsModel,
    null,
    false
  ),
});
